import React, { useCallback } from 'react';
import { useKeysDown } from 'shared/hooks/use-keys-down';

const defaultQuerySelectors = [
  'a[href]:not([disabled])',
  'button:not([disabled])',
  'input',
  'textarea',
  'select',
  'details',
  '[tabindex]:not([tabindex="-1"])',
];

const getChildElements = (container: HTMLElement, querySelectors: string[]) =>
  container.querySelectorAll(
    querySelectors.toString(),
  ) as NodeListOf<HTMLElement>;

export type Options = {
  querySelectors?: string[];
};

export const useFocusChildrenWithArrows = (
  ref: React.RefObject<HTMLElement>,
  { querySelectors = defaultQuerySelectors }: Options = {},
) => {
  const selectPreviousItem = useCallback(
    (e: KeyboardEvent) => {
      if (!ref.current) {
        return;
      }

      const childElements = getChildElements(ref.current, querySelectors);
      const focussedElement = document.activeElement;
      const lastIndex = childElements.length - 1;
      const newElementIndex =
        focussedElement && ref.current?.contains(focussedElement)
          ? [...childElements].indexOf(focussedElement as HTMLElement)
          : lastIndex + 1;

      const newElement =
        childElements[newElementIndex - 1] ?? childElements[lastIndex];

      if (!newElement) {
        return;
      }

      newElement.focus?.();
      e.preventDefault();
    },
    [ref, querySelectors],
  );

  const selectNextItem = useCallback(
    (e: KeyboardEvent) => {
      if (!ref.current) {
        return;
      }

      const childElements = getChildElements(ref.current, querySelectors);
      const focussedElement = document.activeElement;
      const firstIndex = 0;
      const newElementIndex =
        focussedElement && ref.current?.contains(focussedElement)
          ? [...childElements].indexOf(focussedElement as HTMLElement)
          : firstIndex - 1;

      const newElement =
        childElements[newElementIndex + 1] ?? childElements[firstIndex];

      if (!newElement) {
        return;
      }

      newElement.focus?.();
      e.preventDefault();
    },
    [ref, querySelectors],
  );

  useKeysDown(['ArrowUp'], selectPreviousItem, {
    otherKeysInCombinationAllowed: true,
  });
  useKeysDown(['ArrowDown'], selectNextItem, {
    otherKeysInCombinationAllowed: true,
  });
};
